import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import PlaceInput from "./PlaceInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  CheckCircle2,
  Circle,
  Plus,
  X,
  Ship,
  Anchor,
  MapPin,
  Calendar,
  Clock,
  Loader2,
  Trash2,
  Edit,
  Sparkles,
  Package,
  FileText,
  Truck,
  Plane,
  Building2,
  ClipboardCheck,
  ShieldCheck,
  Globe,
  Warehouse,
  Box,
  CheckSquare,
  RefreshCw,
} from "lucide-react";
import {
  MILESTONE_TEMPLATES,
  getMilestoneDisplay,
} from "../constants/milestoneTemplates";

// ── Typed-date helpers ───────────────────────────────────────────────────
// Ops staff TYPE milestone dates (e.g. "23/06/2026") rather than clicking a
// calendar. Native <input type="date"> / datetime-local only commit a value
// once every segment is complete, so a typed/partial entry stays empty and
// the backend records `now()`. We therefore use plain text boxes and parse
// the typed value here. Mirrors the Outlook plugin (OutlookAddinPage.js).
const _pad2 = (n) => String(n).padStart(2, "0");

// Date only → { value: "YYYY-MM-DD" } ("" when blank) or { invalid: true }.
function parseTypedDate(raw) {
  const s = (raw || "").trim();
  if (!s) return { value: "" };
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return { value: `${y}-${_pad2(mo)}-${_pad2(d)}` };
    return { invalid: true };
  }
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    const d = +m[1], mo = +m[2];
    let y = m[3];
    if (y.length === 2) y = "20" + y;
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return { value: `${+y}-${_pad2(mo)}-${_pad2(d)}` };
  }
  return { invalid: true };
}

// Date + OPTIONAL time → { value: "YYYY-MM-DD" or "YYYY-MM-DD HH:mm" } or invalid.
function parseTypedDateTime(raw) {
  const s = (raw || "").trim();
  if (!s) return { value: "" };
  // ISO / stored format with time: YYYY-MM-DD[ T]HH:mm
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})/);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3], h = +m[4], mi = +m[5];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31 && h <= 23 && mi <= 59)
      return { value: `${y}-${_pad2(mo)}-${_pad2(d)} ${_pad2(h)}:${_pad2(mi)}` };
    return { invalid: true };
  }
  // ISO date only
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return { value: `${y}-${_pad2(mo)}-${_pad2(d)}` };
    return { invalid: true };
  }
  // dd/mm/yyyy with optional hh:mm
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (m) {
    const d = +m[1], mo = +m[2];
    let y = m[3];
    if (y.length === 2) y = "20" + y;
    if (!(mo >= 1 && mo <= 12 && d >= 1 && d <= 31)) return { invalid: true };
    if (m[4] !== undefined) {
      const h = +m[4], mi = +m[5];
      if (h > 23 || mi > 59) return { invalid: true };
      return { value: `${+y}-${_pad2(mo)}-${_pad2(d)} ${_pad2(h)}:${_pad2(mi)}` };
    }
    return { value: `${+y}-${_pad2(mo)}-${_pad2(d)}` };
  }
  return { invalid: true };
}

// Stored value (ISO or "YYYY-MM-DD[ HH:mm]") → "dd/mm/yyyy" for the text box.
function storedToTypedDate(stored) {
  if (!stored) return "";
  if (/^\d{1,2}\.\d{1,2}$/.test(stored)) return stored; // legacy "26.03"
  const datePart = stored.replace("T", " ").split(" ")[0];
  const m = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${_pad2(+m[3])}/${_pad2(+m[2])}/${m[1]}`;
  return stored;
}

// Stored value → "dd/mm/yyyy" or "dd/mm/yyyy hh:mm" for the text box.
function storedToTypedDateTime(stored) {
  if (!stored) return "";
  const norm = stored.replace("T", " ");
  const [dp, tp] = norm.split(" ");
  const m = (dp || "").match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return stored;
  const base = `${_pad2(+m[3])}/${_pad2(+m[2])}/${m[1]}`;
  if (tp) {
    const t = tp.match(/^(\d{1,2}):(\d{2})/);
    if (t) return `${base} ${_pad2(+t[1])}:${t[2]}`;
  }
  return base;
}


// ── MilestoneTable ───────────────────────────────────────────────────────
// Renders a single ordered milestone list with its own progress bar.
// Used both for the joint view (one big table) and for each container card
// in the split view.
function MilestoneTable({ containerNo, containerLabel, milestones, onMarkComplete, onEdit, onDelete, onAdd, formatDate }) {
  if (!milestones || milestones.length === 0) {
    if (!containerNo && !containerLabel) return null;
    return (
      <div className="bg-slate-50/60 border-l-4 border-slate-200" data-testid={`milestone-group-${containerNo || "shared"}`}>
        <div className="px-4 py-2.5 flex items-center justify-between bg-white border-b">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-slate-700 font-mono">
              {containerLabel || containerNo || "Shared"}
            </span>
            <span className="text-xs text-slate-400">· 0 milestones</span>
          </div>
          {onAdd ? (
            <Button size="sm" variant="ghost" onClick={onAdd}
                    className="text-xs h-7"
                    data-testid={`milestone-add-${containerNo || "shared"}`}>
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          ) : null}
        </div>
        <div className="p-4 text-center text-xs text-slate-400 italic">
          No milestones for this container yet
        </div>
      </div>
    );
  }

  const total = milestones.length;
  const completed = milestones.filter((m) => m.completed).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  const sorted = [...milestones].sort((a, b) => {
    const getDate = (m) => {
      const dateStr = m.actual_date || m.estimated_date;
      if (!dateStr) return new Date(9999, 11, 31);
      return new Date(dateStr.replace(" ", "T"));
    };
    return getDate(a) - getDate(b);
  });

  return (
    <div className="bg-white" data-testid={`milestone-group-${containerNo || "shared"}`}>
      {/* Container header (only shown in split view) */}
      {containerLabel ? (
        <div className="px-4 py-2.5 flex items-center justify-between bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2 min-w-0">
            <Package className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-slate-700 font-mono truncate"
                  data-testid={`milestone-group-label-${containerNo || "shared"}`}>
              {containerLabel}
            </span>
            <span className="text-xs text-slate-500 whitespace-nowrap">
              · {completed}/{total} completed
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="hidden sm:block w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  percent === 100 ? "bg-green-500" : "bg-blue-500"
                }`}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="text-xs font-medium text-slate-600 w-8 text-right">{percent}%</span>
            {onAdd ? (
              <Button size="sm" variant="ghost" onClick={onAdd}
                      className="text-xs h-7"
                      data-testid={`milestone-add-${containerNo || "shared"}`}>
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Table header */}
      <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50/60 text-xs font-medium text-slate-500 uppercase border-b">
        <div className="col-span-1"></div>
        <div className="col-span-2">Event</div>
        <div className="col-span-3">Location</div>
        <div className="col-span-2">Estimated</div>
        <div className="col-span-3">Actual</div>
        <div className="col-span-1"></div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-slate-100">
        {sorted.map((milestone) => (
          <div
            key={milestone.id}
            className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-slate-50 transition-colors ${
              milestone.completed ? "bg-green-50/50" : ""
            }`}
            data-testid={`milestone-row-${milestone.id}`}
          >
            <div className="col-span-1 flex justify-center">
              {milestone.completed ? (
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              ) : (
                <button onClick={() => onMarkComplete(milestone)} className="group" title="Mark as complete">
                  <Circle className="h-6 w-6 text-slate-300 group-hover:text-green-400 transition-colors" />
                </button>
              )}
            </div>
            <div className="col-span-2 flex items-center gap-1">
              {(() => {
                const display = getMilestoneDisplay(milestone.event_type);
                const Icon = display.icon;
                return <Icon className={`h-4 w-4 ${display.color}`} />;
              })()}
              <span className="font-medium text-slate-700 text-sm">
                {getMilestoneDisplay(milestone.event_type).label}
              </span>
              {milestone.notes && (
                <span className="ml-1 text-red-500 text-xs font-bold" title={milestone.notes}>!</span>
              )}
            </div>
            <div className="col-span-3 flex items-center gap-1">
              <MapPin className="h-3 w-3 text-slate-400" />
              <span className="text-slate-600">{milestone.location}</span>
            </div>
            <div className="col-span-2 text-slate-500 text-sm">{formatDate(milestone.estimated_date)}</div>
            <div className="col-span-3 text-sm">
              {milestone.actual_date ? (
                <span className="text-green-600 font-medium">{formatDate(milestone.actual_date)}</span>
              ) : (
                <span className="text-slate-400">-</span>
              )}
            </div>
            <div className="col-span-1 flex justify-end gap-1">
              <button onClick={() => onEdit(milestone)} className="p-1 hover:bg-slate-200 rounded" title="Edit">
                <Edit className="h-4 w-4 text-slate-400" />
              </button>
              <button onClick={() => onDelete(milestone.id)} className="p-1 hover:bg-red-100 rounded" title="Delete">
                <X className="h-4 w-4 text-slate-400 hover:text-red-500" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export function PFMilestones({ pfId, pfNumber, onClose, containers = [], trackingMode = "joint" }) {
  const [milestones, setMilestones] = useState([]);
  const [progress, setProgress] = useState({ total: 0, completed: 0, percent: 0, status: "Not Started" });
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  
  const [form, setForm] = useState({
    event_type: "arrival",
    location: "",
    estimated_date: "",
    actual_date: "",
    notes: "",
    container_number: "",
  });

  const token = localStorage.getItem("token");

  // ── Per-container split mode ────────────────────────────────────────────
  // Only split when the user explicitly chose `separate` and there's more
  // than one container — otherwise show the legacy single-card view.
  const containerNos = (containers || [])
    .map((c) => (c?.container_number || c || "").toString().trim().toUpperCase())
    .filter(Boolean);
  const splitMode = trackingMode === "separate" && containerNos.length >= 2;

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchMilestones = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/position-folders/${pfId}/milestones`,
        { headers: getAuthHeaders() }
      );
      setMilestones(response.data.milestones || []);
      setProgress(response.data.progress || { total: 0, completed: 0, percent: 0, status: "Not Started" });
    } catch (error) {
      toast.error("Failed to load milestones");
    } finally {
      setLoading(false);
    }
  }, [pfId, getAuthHeaders]);

  useEffect(() => {
    if (pfId) {
      fetchMilestones();
    }
  }, [pfId, fetchMilestones]);

  const resetForm = (containerNumber = "") => {
    setForm({
      event_type: "arrival",
      location: "",
      estimated_date: "",
      actual_date: "",
      notes: "",
      container_number: containerNumber || "",
    });
    setEditingMilestone(null);
  };

  const handleSave = async (forceOverride = false) => {
    // Defensive: if the Add/Update button is wired as `onClick={handleSave}`,
    // React passes the SyntheticEvent as `forceOverride` → axios then tries to
    // JSON.stringify a DOM/fiber object and throws "Converting circular
    // structure to JSON", silently failing the save. Coerce to strict boolean.
    forceOverride = forceOverride === true;
    if (!form.location.trim()) {
      toast.error("Location is required");
      return;
    }

    // Parse the TYPED dates (staff type dd/mm/yyyy rather than picking them).
    // Block on unreadable input so we never silently send an empty date that
    // the backend would turn into now().
    const estParsed = parseTypedDate(form.estimated_date);
    if (estParsed.invalid) {
      toast.error("Estimated Date: type it as dd/mm/yyyy (e.g. 23/06/2026), or leave it blank.");
      return;
    }
    const actParsed = parseTypedDateTime(form.actual_date);
    if (actParsed.invalid) {
      toast.error("Actual Date: type it as dd/mm/yyyy or dd/mm/yyyy hh:mm (e.g. 23/06/2026 14:30), or leave it blank.");
      return;
    }
    // An "actual" date is when the event actually happened — it can't be in the
    // future. Planned/future dates belong in Estimated Date. Block it so we
    // don't mark a future event as completed.
    if (actParsed.value) {
      const todayStr = new Date().toISOString().slice(0, 10);
      if (actParsed.value.slice(0, 10) > todayStr) {
        toast.error("Actual Date can't be in the future — that's when the event happened. Put planned/future dates in 'Estimated Date'.");
        return;
      }
    }
    const payload = {
      ...form,
      estimated_date: estParsed.value,
      actual_date: actParsed.value,
    };

    setSaving(true);
    try {
      if (editingMilestone) {
        await axios.put(
          `${API_URL}/position-folders/${pfId}/milestones/${editingMilestone.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Milestone updated");
      } else {
        await axios.post(
          `${API_URL}/position-folders/${pfId}/milestones`,
          { ...payload, force_override: forceOverride },
          { headers: getAuthHeaders() }
        );
        toast.success("Milestone added");
      }
      setShowAddDialog(false);
      resetForm();
      fetchMilestones();
    } catch (error) {
      // SeaRates source-of-truth conflict (409) — surface the carrier
      // event that blocks this write and offer an explicit override.
      const detail = error?.response?.data?.detail;
      const isConflict = error?.response?.status === 409 &&
        (typeof detail === "object") && detail?.code === "searates_conflict";
      if (isConflict && !editingMilestone) {
        const ok = window.confirm(
          `⚠️ SeaRates conflict\n\n${detail.message}\n\n` +
          `Press OK to override (you confirm the carrier feed is wrong/lagging).\n` +
          `Press Cancel to abort and check the carrier first.`
        );
        if (ok) {
          setSaving(false);
          return handleSave(true);
        }
      } else {
        // Surface the real backend error so the user / agent can actually
        // debug why the save failed (previously this always showed the
        // generic "Failed to save milestone").
        let msg = "Failed to save milestone";
        if (typeof detail === "string") {
          msg = detail;
        } else if (Array.isArray(detail)) {
          // FastAPI Pydantic 422 validation errors: list of {loc, msg, type}
          msg = detail.map((e) => {
            const field = Array.isArray(e?.loc) ? e.loc.slice(-1)[0] : "field";
            return `${field}: ${e?.msg || "invalid"}`;
          }).join("; ");
        } else if (detail && typeof detail === "object") {
          msg = detail.message || detail.detail || JSON.stringify(detail);
        } else if (error?.message) {
          msg = `${error.message}${error?.response?.status ? ` (HTTP ${error.response.status})` : ""}`;
        }
        console.error("[milestone-add] status=", error?.response?.status, "detail=", detail, "raw=", error);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (milestoneId) => {
    if (!window.confirm("Delete this milestone?")) return;
    
    try {
      await axios.delete(
        `${API_URL}/position-folders/${pfId}/milestones/${milestoneId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Milestone deleted");
      fetchMilestones();
    } catch (error) {
      toast.error("Failed to delete milestone");
    }
  };

  const handleEdit = (milestone) => {
    setForm({
      event_type: milestone.event_type,
      location: milestone.location,
      estimated_date: storedToTypedDate(milestone.estimated_date || ""),
      actual_date: storedToTypedDateTime(milestone.actual_date || ""),
      notes: milestone.notes || "",
      container_number: milestone.container_number || "",
    });
    setEditingMilestone(milestone);
    setShowAddDialog(true);
  };

  const handleAutoGenerate = async () => {
    if (milestones.length > 0) {
      if (!window.confirm("This will replace existing milestones. Continue?")) return;
      // Delete existing milestones first
      for (const m of milestones) {
        await axios.delete(
          `${API_URL}/position-folders/${pfId}/milestones/${m.id}`,
          { headers: getAuthHeaders() }
        );
      }
    }

    setGenerating(true);
    try {
      await axios.post(
        `${API_URL}/position-folders/${pfId}/milestones/auto-generate`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Milestones generated from PF data");
      fetchMilestones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate milestones");
    } finally {
      setGenerating(false);
    }
  };

  // Re-sync SeaRates tracking for EACH container of the PF so every
  // container ends up with its OWN timeline. Only meaningful in split mode.
  const handleResyncPerContainer = async () => {
    if (!splitMode) return;
    if (!window.confirm(
      `Re-fetch SeaRates tracking separately for ${containerNos.length} containers? ` +
      `This will add per-container milestones. Existing milestones are kept; ` +
      `duplicates within 5 minutes are skipped.`
    )) return;
    setResyncing(true);
    try {
      const r = await axios.post(
        `${API_URL}/position-folders/${pfId}/resync-tracking-per-container`,
        {},
        { headers: getAuthHeaders() }
      );
      const ok = r.data?.containers_resynced ?? 0;
      const total = r.data?.containers_total ?? containerNos.length;
      toast.success(`Re-synced ${ok}/${total} containers — refreshing milestones…`);
      fetchMilestones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Re-sync failed");
    } finally {
      setResyncing(false);
    }
  };


  const handleMarkComplete = async (milestone) => {
    const now = new Date().toISOString().slice(0, 16).replace("T", " ");
    try {
      await axios.put(
        `${API_URL}/position-folders/${pfId}/milestones/${milestone.id}`,
        { actual_date: now },
        { headers: getAuthHeaders() }
      );
      const display = getMilestoneDisplay(milestone.event_type);
      toast.success(`${display.label} at ${milestone.location} marked complete`);
      fetchMilestones();
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      // Handle short format like "26.03" (day.month) - return as-is
      if (/^\d{1,2}\.\d{1,2}$/.test(dateStr)) {
        return dateStr;
      }
      
      // Handle both ISO dates and "YYYY-MM-DD HH:mm" format
      const date = new Date(dateStr.replace(" ", "T"));
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateStr; // Return original if invalid
      }
      
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const hasTime = dateStr.includes(" ") || dateStr.includes("T");
      
      if (hasTime) {
        const hours = date.getHours().toString().padStart(2, "0");
        const mins = date.getMinutes().toString().padStart(2, "0");
        return `${day}.${month}.${year} ${hours}:${mins}`;
      }
      return `${day}.${month}.${year}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{progress.status}</h2>
            <p className="text-blue-100 text-sm">Milestone Tracker - {pfNumber}</p>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-blue-500">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 py-3 bg-slate-50 border-b">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-6 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${Math.max(progress.percent, 10)}%` }}
            >
              {progress.percent}%
            </div>
          </div>
          <span className="text-sm text-slate-600 whitespace-nowrap">
            {progress.completed}/{progress.total} completed
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="p-3 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Calendar className="h-4 w-4" />
          <span>Statuses</span>
          {splitMode && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">
              Per-container · {containerNos.length} containers
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {splitMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResyncPerContainer}
              disabled={resyncing}
              className="text-xs"
              data-testid="milestones-resync-per-container-btn"
              title="Re-fetch SeaRates tracking separately for each container"
            >
              {resyncing
                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                : <RefreshCw className="h-3 w-3 mr-1" />}
              Re-sync per container
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleAutoGenerate}
            disabled={generating}
            className="text-xs"
            data-testid="milestones-auto-generate-btn"
          >
            {generating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
            Auto-Generate
          </Button>
          <Button 
            size="sm" 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="text-xs bg-blue-600 hover:bg-blue-700"
            data-testid="milestones-add-btn"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Milestones List */}
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" />
          <p className="text-sm text-slate-500 mt-2">Loading milestones...</p>
        </div>
      ) : milestones.length === 0 ? (
        <div className="p-8 text-center">
          <Ship className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">No milestones yet</p>
          <p className="text-sm text-slate-400">Click "Auto-Generate" or "Add" to create milestones</p>
        </div>
      ) : splitMode ? (
        // ── PER-CONTAINER SPLIT VIEW ────────────────────────────────────
        // Spec (Rok, 2026-02): when `tracking_mode === "separate"` each
        // container has its OWN SeaRates / vessel / port timeline with its
        // OWN dates. We render strictly by `container_number`:
        //   • TriNet / TLM (source starts with "trinet…")  → SHARED bucket
        //     (PF-level customs MRN events apply to the whole booking).
        //   • Any milestone with a `container_number`      → under that
        //     container ONLY. No fan-out — copying one container's dates
        //     to another would lie to the operator.
        //   • Untagged non-TriNet milestones (legacy data) → SHARED, with
        //     a banner inviting the operator to "Re-sync per container".
        (() => {
          const groups = new Map();
          for (const c of containerNos) groups.set(c, []);
          const shared = [];
          const legacyUntagged = [];

          const isTrinet = (m) =>
            typeof m.source === "string" && m.source.toLowerCase().startsWith("trinet");

          for (const m of milestones) {
            const cn = (m.container_number || "").toString().trim().toUpperCase();
            if (cn) {
              if (!groups.has(cn)) groups.set(cn, []);
              groups.get(cn).push(m);
            } else if (isTrinet(m)) {
              shared.push(m);
            } else {
              legacyUntagged.push(m);
            }
          }

          const cards = [];
          if (shared.length > 0 || legacyUntagged.length > 0) {
            cards.push({
              key: "__shared__",
              label: shared.length > 0
                ? "Shared · all containers (TriNet / TLM / PF-level)"
                : "Shared · legacy auto-imported (run Re-sync per container)",
              cn: "",
              list: [...shared, ...legacyUntagged],
            });
          }
          for (const [cn, list] of groups.entries()) {
            cards.push({ key: cn, label: cn, cn, list });
          }
          return (
            <div className="divide-y-2 divide-slate-200">
              {cards.map((card) => (
                <MilestoneTable
                  key={card.key}
                  containerNo={card.cn}
                  containerLabel={card.label}
                  milestones={card.list}
                  onMarkComplete={handleMarkComplete}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAdd={() => { resetForm(card.cn); setShowAddDialog(true); }}
                  formatDate={formatDate}
                />
              ))}
            </div>
          );
        })()
      ) : (
        // ── LEGACY JOINT VIEW ──────────────────────────────────────────
        <MilestoneTable
          milestones={milestones}
          onMarkComplete={handleMarkComplete}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatDate={formatDate}
        />
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(() => {
                const display = getMilestoneDisplay(form.event_type);
                const Icon = display.icon;
                return <Icon className={`h-5 w-5 ${display.color}`} />;
              })()}
              {editingMilestone ? "Edit Milestone" : "Add Milestone"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {splitMode && (
              <div>
                <Label>Container</Label>
                <select
                  value={form.container_number || ""}
                  onChange={(e) => setForm({ ...form, container_number: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="milestone-container-select"
                >
                  <option value="">Shared · all containers</option>
                  {containerNos.map((cn) => (
                    <option key={cn} value={cn}>{cn}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Pick which container this milestone belongs to. Leave blank for shared events (e.g. origin pickup).
                </p>
              </div>
            )}

            <div>
              <Label>Event Type</Label>
              <select
                value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(MILESTONE_TEMPLATES).map(([groupName, group]) => (
                  <optgroup key={groupName} label={groupName}>
                    {group.items.map(item => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {form.event_type && (
                <p className="text-xs text-muted-foreground mt-1">
                  Selected: <span className="font-medium">{getMilestoneDisplay(form.event_type).label}</span>
                </p>
              )}
            </div>

            <div>
              <Label>Location *</Label>
              <PlaceInput
                value={form.location}
                kind="all"
                allowFreeText={true}
                placeholder="e.g., Shanghai, Koper, Warehouse A"
                data-testid="milestone-location"
                onSelect={(p) => setForm({ ...form, location: p.code ? `${p.name} (${p.code})` : (p.name || "") })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Date</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/yyyy"
                  value={form.estimated_date}
                  onChange={(e) => setForm({ ...form, estimated_date: e.target.value })}
                  data-testid="milestone-estimated-date-input"
                />
              </div>
              <div>
                <Label>Actual Date & Time</Label>
                <Input
                  type="text"
                  placeholder="dd/mm/yyyy hh:mm"
                  value={form.actual_date}
                  onChange={(e) => setForm({ ...form, actual_date: e.target.value })}
                  data-testid="milestone-actual-date-input"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Type dates as <strong>dd/mm/yyyy</strong> (e.g. 23/06/2026). Time is
              optional on the actual date (e.g. 23/06/2026 14:30).
            </p>

            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={() => handleSave()} disabled={saving} data-testid="milestone-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingMilestone ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PFMilestones;
